/**
 * 설정 > 감사 로그 UI 타입·헬퍼·목 데이터.
 * API `AdminAuditLogEventType`과 라벨을 맞추고, filter 헬퍼는 단위 테스트용으로 유지한다.
 */
import type {
  AdminAuditChange,
  AdminAuditLog,
  AdminAuditLogEventType,
} from '@/entities/admin-user/types';
import type { BadgeProps } from '@/shared/components/ui/badge';
import { format } from 'date-fns';
import { formatAdminDateTime } from './settingsAccountData';

export type AuditEventType = AdminAuditLogEventType;

export type AuditLogRow = {
  id: string;
  /** `YYYY.MM.DD HH:mm:ss` */
  at: string;
  event: AuditEventType;
  actorDisplay: string;
  targetDisplay: string;
  targetMenuName: string;
  /** 목록 셀용 짧은 요약 */
  summary: string;
  /** API changes — 상세 표시용 */
  changes: AdminAuditChange[];
  /** changes 없을 때 폴백: 변경 전 스냅샷 */
  beforeSnapshot: string | null;
  /** changes 없을 때 폴백: 변경 후 스냅샷 */
  afterSnapshot: string | null;
  ip: string;
};

export const AUDIT_EVENT_LABELS: Record<AuditEventType, string> = {
  ADMIN_USER_CREATED: '계정 생성',
  ADMIN_USER_UPDATED: '계정 수정',
  ADMIN_USER_SELF_UPDATED: '본인 정보 수정',
  ADMIN_USER_DELETED: '계정 삭제',
  ADMIN_USER_LOCKED: '계정 잠금',
  ADMIN_USER_UNLOCKED: '잠금 해제',
  ADMIN_USER_TEMPORARY_PASSWORD_RESET: '비밀번호 초기화',
  MENU_PERMISSION_UPDATED: '권한 변경',
  MENU_CREATED: '메뉴 생성',
  MENU_UPDATED: '메뉴 수정',
  MENU_DELETED: '메뉴 삭제',
  MENU_TREE_REORDERED: '메뉴 순서 변경',
};

export const getAuditEventLabel = (event: AuditEventType): string =>
  AUDIT_EVENT_LABELS[event] ?? event;

/** Badge variant + Figma 보라(권한 변경) 오버라이드 */
export const getAuditEventBadgeProps = (
  event: AuditEventType,
): {
  variant: NonNullable<BadgeProps['variant']>;
  className?: string;
} => {
  switch (event) {
    case 'ADMIN_USER_CREATED':
      return { variant: 'success' };
    case 'ADMIN_USER_UPDATED':
    case 'ADMIN_USER_SELF_UPDATED':
      return { variant: 'info' };
    case 'ADMIN_USER_LOCKED':
      return {
        variant: 'outline',
        className: 'border-ruda-warning/30 bg-ruda-warning-50 text-ruda-warning',
      };
    case 'ADMIN_USER_UNLOCKED':
      return {
        variant: 'outline',
        className: 'border-ruda-success/30 bg-ruda-success-50 text-ruda-success',
      };
    case 'ADMIN_USER_TEMPORARY_PASSWORD_RESET':
      return { variant: 'warning' };
    case 'MENU_PERMISSION_UPDATED':
      return {
        variant: 'outline',
        className: 'border-[#5940cc]/25 bg-[#f0edfc] text-[#5940cc]',
      };
    case 'MENU_CREATED':
      return { variant: 'success' };
    case 'MENU_UPDATED':
      return { variant: 'info' };
    case 'MENU_TREE_REORDERED':
      return {
        variant: 'outline',
        className: 'border-ruda-info/25 bg-ruda-info-50 text-ruda-info',
      };
    case 'MENU_DELETED':
    case 'ADMIN_USER_DELETED':
      return { variant: 'destructive' };
    default:
      return { variant: 'outline' };
  }
};

const fallbackText = (value: string | null | undefined): string => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : '—';
};

const formatAuditMenuDisplay = (
  menuName: string | null | undefined,
  menuKey: string | null | undefined,
): string => fallbackText(menuName ?? menuKey);

const formatAuditUserDisplay = (
  username: string | null | undefined,
  email: string | null | undefined,
  options?: { hideEmptyParentheses?: boolean },
): string => {
  const safeUsername = fallbackText(username);
  const safeEmail = fallbackText(email);
  if (
    options?.hideEmptyParentheses &&
    safeUsername === '—' &&
    safeEmail === '—'
  ) {
    return '—';
  }
  return `${safeUsername} (${safeEmail})`;
};

/** 필터 기준 시각 — 목 데이터(2026-07)와 «최근 N일»이 맞도록 고정 */
export const AUDIT_FILTER_REFERENCE_NOW = new Date('2026-07-06T23:59:59');

export type AuditPeriodFilter = '7d' | '30d' | 'all';

export const AUDIT_PERIOD_OPTIONS: {
  value: AuditPeriodFilter;
  label: string;
}[] = [
  { value: '7d', label: '최근 7일' },
  { value: '30d', label: '최근 30일' },
  { value: 'all', label: '전체' },
];

export const AUDIT_EVENT_FILTER_OPTIONS: {
  value: string;
  label: string;
}[] = [
  { value: 'all', label: '전체' },
  ...(Object.entries(AUDIT_EVENT_LABELS) as [AuditEventType, string][]).map(
    ([value, label]) => ({ value, label }),
  ),
];

const truncateText = (value: string | null | undefined, max = 80): string => {
  if (!value) return '—';
  const trimmed = value.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max)}…`;
};

/**
 * 감사 스냅샷 표시용.
 * JSON이면 2칸 들여쓰기로 pretty-print, 아니면 원문 유지.
 */
export const formatAuditSnapshotPretty = (
  value: string | null | undefined,
): string => {
  if (!value?.trim()) return '—';
  const trimmed = value.trim();
  try {
    const parsed: unknown = JSON.parse(trimmed);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return value;
  }
};

const formatAuditChangeValue = (value: string | null | undefined): string => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : '—';
};

/** changes 한 항목 → `라벨: 변경전 → 변경후` */
export const formatAuditChangeLine = (change: AdminAuditChange): string =>
  `${change.label}: ${formatAuditChangeValue(change.before)} → ${formatAuditChangeValue(change.after)}`;

/** 목록·요약용 — changes 우선, 없으면 스냅샷 폴백 */
export const formatAuditChangesSummary = (
  log: Pick<AdminAuditLog, 'changes' | 'beforeSnapshot' | 'afterSnapshot'>,
  max = 80,
): string => {
  const changes = log.changes ?? [];
  if (changes.length > 0) {
    return truncateText(changes.map(formatAuditChangeLine).join(' · '), max);
  }
  return truncateText(log.afterSnapshot ?? log.beforeSnapshot, max);
};

export const mapAdminAuditLogToRow = (log: AdminAuditLog): AuditLogRow => ({
  id: String(log.id),
  at: formatAdminDateTime(log.occurredAt) ?? log.occurredAt,
  event: log.eventType,
  actorDisplay: formatAuditUserDisplay(log.actorUsername, log.actorEmail),
  targetDisplay: formatAuditUserDisplay(log.targetUsername, log.targetEmail, {
    hideEmptyParentheses: true,
  }),
  targetMenuName: formatAuditMenuDisplay(log.targetMenuName, log.targetMenuKey),
  summary: formatAuditChangesSummary(log),
  changes: log.changes ?? [],
  beforeSnapshot: log.beforeSnapshot,
  afterSnapshot: log.afterSnapshot,
  ip: log.clientIp ?? '—',
});

/**
 * 기간 필터 → API startDate/endDate (LocalDateTime).
 * 서버는 `yyyy-MM-dd'T'HH:mm:ss` 형식만 수신한다.
 */
export const toAuditLogDateRange = (
  period: AuditPeriodFilter,
  now = new Date(),
): { startDate?: string; endDate?: string } => {
  if (period === 'all') return {};
  const end = new Date(now);
  const start = new Date(now);
  start.setDate(end.getDate() - (period === '7d' ? 7 : 30));
  return {
    startDate: format(start, "yyyy-MM-dd'T'00:00:00"),
    endDate: format(end, "yyyy-MM-dd'T'23:59:59"),
  };
};

/** Figma 샘플 6건 — 단위 테스트용 (API 이벤트 타입) */
export const MOCK_AUDIT_LOGS: AuditLogRow[] = [
  {
    id: 'a1',
    at: '2026-07-06 09:40',
    event: 'ADMIN_USER_CREATED',
    actorDisplay: 'master (master@rudalink.co.kr)',
    targetDisplay: 'fin.choi (fin.choi@rudalink.co.kr)',
    targetMenuName: '—',
    summary: '역할: 일반 · 메뉴 권한 4건 부여',
    changes: [],
    beforeSnapshot: null,
    afterSnapshot: '역할: 일반 · 메뉴 권한 4건 부여',
    ip: '10.0.1.12',
  },
  {
    id: 'a2',
    at: '2026-07-06 09:41',
    event: 'ADMIN_USER_TEMPORARY_PASSWORD_RESET',
    actorDisplay: 'master (master@rudalink.co.kr)',
    targetDisplay: 'fin.choi (fin.choi@rudalink.co.kr)',
    targetMenuName: '—',
    summary: '임시 비밀번호 발급',
    changes: [],
    beforeSnapshot: null,
    afterSnapshot: '임시 비밀번호 발급',
    ip: '10.0.1.12',
  },
  {
    id: 'a3',
    at: '2026-07-05 17:02',
    event: 'MENU_PERMISSION_UPDATED',
    actorDisplay: 'admin.park (admin.park@rudalink.co.kr)',
    targetDisplay: 'cs.lee (cs.lee@rudalink.co.kr)',
    targetMenuName: '설정',
    summary: '회원 관리: R → RW',
    changes: [
      {
        field: 'menuPermission',
        label: '회원 관리',
        before: 'R',
        after: 'RW',
      },
    ],
    beforeSnapshot: '회원 관리: R',
    afterSnapshot: '회원 관리: RW',
    ip: '10.0.1.8',
  },
  {
    id: 'a4',
    at: '2026-07-03 11:20',
    event: 'ADMIN_USER_UPDATED',
    actorDisplay: 'admin.park (admin.park@rudalink.co.kr)',
    targetDisplay: 'mkt.jung (mkt.jung@rudalink.co.kr)',
    targetMenuName: '—',
    summary: '소속: 운영팀 → 마케팅',
    changes: [
      {
        field: 'department',
        label: '소속',
        before: '운영팀',
        after: '마케팅',
      },
    ],
    beforeSnapshot: '소속: 운영팀',
    afterSnapshot: '소속: 마케팅',
    ip: '10.0.1.8',
  },
  {
    id: 'a5',
    at: '2026-07-01 08:15',
    event: 'ADMIN_USER_UNLOCKED',
    actorDisplay: 'master (master@rudalink.co.kr)',
    targetDisplay: 'mkt.jung (mkt.jung@rudalink.co.kr)',
    targetMenuName: '—',
    summary: '로그인 5회 실패 잠금 해제',
    changes: [],
    beforeSnapshot: null,
    afterSnapshot: '로그인 5회 실패 잠금 해제',
    ip: '10.0.1.12',
  },
  {
    id: 'a6',
    at: '2026-06-28 14:33',
    event: 'ADMIN_USER_DELETED',
    actorDisplay: 'master (master@rudalink.co.kr)',
    targetDisplay: 'ops.kang (ops.kang@rudalink.co.kr)',
    targetMenuName: '—',
    summary: '상태: 비활성 → 삭제',
    changes: [
      {
        field: 'status',
        label: '상태',
        before: '비활성',
        after: '삭제',
      },
    ],
    beforeSnapshot: '상태: 비활성',
    afterSnapshot: '상태: 삭제',
    ip: '10.0.1.12',
  },
];

const parseAuditAt = (at: string): Date => {
  // `YYYY-MM-DD HH:mm` → ISO-ish
  return new Date(at.replace(' ', 'T') + ':00');
};

export const listAuditActorOptions = (
  logs: AuditLogRow[],
): { value: string; label: string }[] => {
  const emails = [...new Set(logs.map((row) => row.actorDisplay))].sort();
  return [
    { value: 'all', label: '전체' },
    ...emails.map((email) => ({ value: email, label: email })),
  ];
};

export const filterAuditLogs = (
  logs: AuditLogRow[],
  params: {
    period: AuditPeriodFilter;
    event: string;
    actor: string;
    targetQuery: string;
    now?: Date;
  },
): AuditLogRow[] => {
  const now = params.now ?? AUDIT_FILTER_REFERENCE_NOW;
  const q = params.targetQuery.trim().toLowerCase();

  return logs.filter((row) => {
    if (params.event !== 'all' && row.event !== params.event) return false;
    if (params.actor !== 'all' && row.actorDisplay !== params.actor) return false;
    if (q && !row.targetDisplay.toLowerCase().includes(q)) return false;

    if (params.period !== 'all') {
      const at = parseAuditAt(row.at);
      if (Number.isNaN(at.getTime())) return false;
      const days = params.period === '7d' ? 7 : 30;
      const min = new Date(now);
      min.setDate(min.getDate() - days);
      if (at < min || at > now) return false;
    }

    return true;
  });
};
